import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useAppId } from '../../context/AppIdContext';
import Header from '../../component/header';
import '../../style/dashboard.css';

const BASE_URL = process.env.REACT_APP_BASE_URL;

const PollingLocationWiseLiveVoting = () => {
    const navigate = useNavigate();
    const { appId, language } = useAppId();
    const lang = language || 'en';

    // --- Translation Dictionary ---
    const t = {
        en: {
            pageTitle: "Polling Location Wise Live Voting",
            pollingLocation: "Polling Location :",
            totalVoter: "Voters",
            totalVoting: "Total Voting",
            remainVoting: "Remain Voting",
            noData: "No data available.",
            loading: "Fetching Polling Location data...",
            searchPlaceholder: "Search by Polling Location..."
        },
        hi: {
            pageTitle: "मतदान स्थल अनुसार लाइव वोटिंग",
            pollingLocation: "मतदान स्थल :",
            totalVoter: "मतदाता",
            totalVoting: "कुल वोटिंग",
            remainVoting: "बाकी वोटिंग",
            noData: "कोई डेटा उपलब्ध नहीं है।",
            loading: "डेटा लोड हो रहा है...",
            searchPlaceholder: "मतदान स्थल से खोजें..."
        },
        gu: {
            pageTitle: "મતદાન સ્થળ વાઇઝ લાઈવ મતદાન",
            pollingLocation: "મતદાન સ્થળ :",
            totalVoter: "મતદાર",
            totalVoting: "કુલ વોટિંગ",
            remainVoting: "બાકી વોટિંગ",
            noData: "કોઈ માહિતી ઉપલબ્ધ નથી.",
            loading: "માહિતી લોડ થઈ રહી છે...",
            searchPlaceholder: "મતદાન સ્થળ દ્વારા શોધો..."
        }
    };

    const txt = t[lang] || t.gu;

    const [loading, setLoading] = useState(false);
    const [pollingLocations, setPollingLocations] = useState([]);
    const [colorThresholds, setColorThresholds] = useState([]);
    const [searchTerm, setSearchTerm] = useState("");

    useEffect(() => {
        if (!appId) {
            navigate('/login');
            return;
        }

        const fetchPollingLocationData = async () => {
            setLoading(true);
            try {
                const response = await axios.post(`${BASE_URL}/api/dis_polling_location_wise_voting_report`, {
                    app_id: appId
                });
                
                if (response.data.success && response.data.data) {
                    setPollingLocations(response.data.data.result || []);
                    
                    // Sort the color thresholds ascending by upto_percent for correct evaluation
                    const sortedColors = (response.data.data.result2 || []).sort((a, b) => a.upto_percent - b.upto_percent);
                    setColorThresholds(sortedColors);
                } else {
                    setPollingLocations([]);
                }
            } catch (error) {
                console.error("Error fetching polling location voting report:", error);
                setPollingLocations([]);
            } finally {
                setLoading(false);
            }
        };

        fetchPollingLocationData();
        // Auto refresh every 60 seconds to keep live data fresh
        const interval = setInterval(fetchPollingLocationData, 60000);
        return () => clearInterval(interval);

    }, [appId, navigate]);

    // Function to determine the header color based on result2 array
    const getHeaderColor = (percent) => {
        // Default color if array is empty or percent is higher than max threshold
        let selectedColor = "#d68e09"; 
        
        for (let i = 0; i < colorThresholds.length; i++) {
            if (percent <= colorThresholds[i].upto_percent) {
                selectedColor = colorThresholds[i].color_code;
                break; // Stop at the first threshold met
            }
        }
        return selectedColor;
    };

    // Function to truncate long location names
    const truncateLocation = (location) => {
        if (!location) return "";
        if (location.length > 60) {
            return location.substring(0, 57) + "...";
        }
        return location;
    };

    // Filter by search
    const filteredLocations = pollingLocations.filter(location => {
        if (!searchTerm) return true;
        const lowerSearch = searchTerm.toLowerCase();
        return location.eng_polling_location && location.eng_polling_location.toLowerCase().includes(lowerSearch);
    });

    return (
        <div className="plw-live-voting-page">
            <div className="header-wrapper shadow-sm">
                <Header title={txt.pageTitle} />
            </div>

            <div className="content-container container-fluid p-3 p-md-4 mx-auto">
                
                {/* Search Bar */}
                <div className="search-wrapper mb-4 slide-up-element" style={{maxWidth:"500px"}}>
                    <svg className="search-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                    <input
                        type="text"
                        className="search-input"
                        placeholder={txt.searchPlaceholder}
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                </div>

                {loading && pollingLocations.length === 0 ? (
                    <div className="text-center p-5">
                        <div className="spinner-border text-primary" role="status"></div>
                        <p className="mt-2 text-muted">{txt.loading}</p>
                    </div>
                ) : filteredLocations.length > 0 ? (
                    <div className="row g-4 slide-up-element" style={{ animationDelay: '0.1s' }}>
                        {filteredLocations.map((location, index) => {
                            const headerColor = getHeaderColor(location.voting_percent);
                            
                            return (
                                <div className="col-12 col-sm-6 col-lg-6 col-xl-4" key={index} onClick={() => navigate("/dis_polling_location_wise_live_voting_voter", { state: { pollingLocation: location.eng_polling_location} })}>
                                    <div className="voting-card h-100">
                                        
                                        {/* Card Header (Dynamic Color) */}
                                        <div className="vc-header" style={{ backgroundColor: headerColor }}>
                                            <div className="vc-header-info">
                                                <div className="vc-location-badge">
                                                    <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                                                        <path strokeLinecap="round" strokeLinejoin="round" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                                                    </svg>
                                                </div>
                                                <h3 className="vc-location-title" title={location.eng_polling_location}>
                                                    {truncateLocation(location.eng_polling_location)}
                                                </h3>
                                            </div>
                                            <div className="vc-percentage-pill">
                                                {location.voting_percent}%
                                            </div>
                                        </div>

                                        {/* Card Body (Stats) */}
                                        <div className="vc-body">
                                            <div className="vc-stat-box bg-light-blue">
                                                <span className="vc-lbl text-blue">{txt.totalVoter}</span>
                                                <span className="vc-num text-blue">{location.total_voter}</span>
                                            </div>
                                            <div className="vc-stat-box bg-light-green">
                                                <span className="vc-lbl text-green">{txt.totalVoting}</span>
                                                <span className="vc-num text-green">{location.total_voting}</span>
                                            </div>
                                            <div className="vc-stat-box bg-light-red">
                                                <span className="vc-lbl text-red">{txt.remainVoting}</span>
                                                <span className="vc-num text-red">{location.total_remain}</span>
                                            </div>
                                        </div>

                                    </div>
                                </div>
                            );
                        })}
                    </div>
                ) : (
                    <div className="text-center text-muted p-4 bg-white rounded shadow-sm border slide-up-element">
                        {txt.noData}
                    </div>
                )}
            </div>

            <style jsx>{`
                .plw-live-voting-page { width: 100%; min-height: 100vh; background: #eef2f6; font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif; padding-bottom: 40px; }
                .header-wrapper { background: white; padding: 0 24px; position: sticky; top: 0; z-index: 50; }

                /* Search Bar */
                .search-wrapper { position: relative; width: 100%; }
                .search-icon { position: absolute; left: 16px; top: 50%; transform: translateY(-50%); color: #94a3b8; width: 20px; height: 20px; }
                .search-input { width: 100%; padding: 14px 16px 14px 46px; border: 1px solid #cbd5e1; border-radius: 12px; font-size: 15px; color: #1e293b; background-color: #ffffff; outline: none; transition: all 0.2s ease; box-shadow: 0 2px 4px rgba(0,0,0,0.02); }
                .search-input:focus { border-color: #3b82f6; box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1); }

                /* Voting Card */
                .voting-card {
                    background: #ffffff;
                    border-radius: 16px;
                    border: 1px solid #cbd5e1;
                    box-shadow: 0 4px 6px -1px rgba(0,0,0,0.05);
                    overflow: hidden;
                    display: flex;
                    flex-direction: column;
                    transition: transform 0.2s ease;
                    height: 100%;
                }
                .voting-card:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 10px 15px -3px rgba(0,0,0,0.1);
                }

                /* Header */
                .vc-header {
                    padding: 16px 20px;
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    gap: 12px;
                    flex-wrap: wrap;
                }
                .vc-header-info {
                    flex: 1;
                    display: flex;
                    align-items: center;
                    gap: 10px;
                    min-width: 0;
                }
                .vc-location-badge {
                    background: rgba(255, 255, 255, 0.2);
                    width: 32px;
                    height: 32px;
                    border-radius: 8px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    flex-shrink: 0;
                }
                .vc-location-badge svg {
                    color: white;
                }
                .vc-location-title {
                    margin: 0;
                    color: white;
                    font-size: 0.9rem;
                    font-weight: 600;
                    line-height: 1.4;
                    word-break: break-word;
                    flex: 1;
                }
                .vc-percentage-pill {
                    background: white;
                    color: #1e293b;
                    font-weight: 800;
                    font-size: 14px;
                    padding: 4px 14px;
                    border-radius: 20px;
                    box-shadow: 0 2px 4px rgba(0,0,0,0.1);
                    white-space: nowrap;
                }

                /* Body / Stats */
                .vc-body {
                    display: flex;
                    justify-content: space-between;
                    gap: 12px;
                    padding: 16px;
                    background: #ffffff;
                    flex: 1;
                }
                .vc-stat-box {
                    flex: 1;
                    border-radius: 10px;
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    justify-content: center;
                    padding: 16px 8px;
                    text-align: center;
                }

                /* Color Themes for Boxes */
                .bg-light-blue { background-color: #eff6ff; }
                .text-blue { color: #1e40af; }

                .bg-light-green { background-color: #f0fdf4; }
                .text-green { color: #166534; }

                .bg-light-red { background-color: #fef2f2; }
                .text-red { color: #991b1b; }

                /* Text formatting inside boxes */
                .vc-lbl {
                    font-size: 11px;
                    font-weight: 700;
                    margin-bottom: 4px;
                    text-transform: uppercase;
                    text-align: center;
                }
                .vc-num {
                    font-size: 22px;
                    font-weight: 800;
                    line-height: 1;
                }

                /* Animation */
                .slide-up-element { animation: slideUp 0.4s ease forwards; opacity: 0; }
                @keyframes slideUp { from { opacity: 0; transform: translateY(15px); } to { opacity: 1; transform: translateY(0); } }

                /* Responsive Grid Breakpoints */
                @media (max-width: 768px) {
                    .vc-body { gap: 8px; padding: 12px; }
                    .vc-stat-box { padding: 12px 4px; }
                    .vc-lbl { font-size: 9px; }
                    .vc-num { font-size: 18px; }
                    .vc-header { padding: 12px 16px; }
                    .vc-location-title { font-size: 0.8rem; }
                    .vc-percentage-pill { font-size: 11px; padding: 3px 10px; }
                    .vc-location-badge { width: 28px; height: 28px; }
                    .vc-location-badge svg { width: 14px; height: 14px; }
                }

                @media (max-width: 480px) {
                    .vc-stat-box { padding: 10px 2px; }
                    .vc-num { font-size: 16px; }
                    .vc-lbl { font-size: 8px; }
                    .vc-location-title { font-size: 0.75rem; }
                }
            `}</style>
        </div>
    );
};

export default PollingLocationWiseLiveVoting;